import { List, Stack, Typography } from '@mui/material'
import { Icons, MenuListItem } from 'utoniq-ui'

import { ArtistPaymentType, Maybe } from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useAuth, User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { openIntercom, updateIntercom } from '@/lib/intercom'

export * from './fragments'

export type State = {
  artistId: string
  user: Maybe<User>
  paymentType: Maybe<ArtistPaymentType>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ConsoleAccountSettingTemplate: Template<State, Action> = ({
  state,
}) => {
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleAccountSetting} />

      <ConsoleBaseLayout
        header={{
          left: <ArtistHeaderItemsLeft />,
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <HeadingTextCell />
        <AccountManageListCell
          user={state.user}
          paymentType={state.paymentType}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const HeadingTextCell: Cell = () => {
  const { t } = useTranslation('consoleAccountSetting')
  return (
    <CellFrame sx={{ px: 5, pt: 2 }}>
      <Typography variant="h3" sx={{ py: 3 }}>
        {t('accountSetting')}
      </Typography>
    </CellFrame>
  )
}
type AccountManageListCellProps = {
  user: State['user']
  paymentType: State['paymentType']
}
const AccountManageListCell: Cell<AccountManageListCellProps> = ({
  user,
  paymentType,
}) => {
  const { t } = useTranslation('consoleAccountSetting')
  const { routerPages } = useRouter()
  const { isBoundWithSNS } = useAuth()

  const manages = [
    {
      title: t('emailAddressSetting'),
      href: routerPages.EmailSetting,
      secondaryAction: (
        <Stack alignItems="center">
          <Icons.Window />
        </Stack>
      ),
    },
    {
      title: t('passwordSetting'),
      href: routerPages.PasswordSetting,
      secondaryAction: (
        <Stack alignItems="center">
          <Icons.Window />
        </Stack>
      ),
    },
    {
      title: t('integrations'),
      href: routerPages.Integrations,
      secondaryAction: (
        <Stack alignItems="center">
          <Icons.Window />
        </Stack>
      ),
    },
    {
      title: t('profileSetting'),
      href: routerPages.ConsoleMyProfileEdit,
    },
    {
      title: t('accountInformationSetting'),
      href: routerPages.ConsoleBankEdit,
    },
    {
      title: t('businessInformationSetting'),
      href: routerPages.ConsoleBusinessEdit,
    },
    {
      title: t('cryptoWalletSetting'),
      href: routerPages.ConsoleCryptocurrencyWallet,
    },
    {
      title: t('inquiry'),
      component: 'button',
      onClick() {
        updateIntercom(user)
        openIntercom()
      },
    },
  ]
  return (
    <CellFrame sx={{ py: 2 }}>
      <List>
        {manages.map(manage => {
          if (
            paymentType !== ArtistPaymentType.Connect &&
            (manage.title === t('accountInformationSetting') ||
              manage.title === t('businessInformationSetting'))
          ) {
            return null
          }

          if (
            (!config.platform.includes('web') ||
              /**
               * @description
               * Diica以外の場合、SNS認証機能解放
               * （DiicaはSNS連携時のUtoniqアイコンの表示をしてはいけないため、SNSリンク機能を隠す）
               */ config.applicationKey === 'diica') &&
            manage.title === t('integrations')
          )
            return null

          if (!config.nft && manage.title === t('cryptoWalletSetting'))
            return null

          if (manage.title === t('passwordSetting') && isBoundWithSNS)
            return null

          return (
            <MenuListItem
              key={manage.title}
              divider
              sx={{ bgcolor: 'grey.50', width: '100%' }}
              {...manage}
            />
          )
        })}
      </List>
    </CellFrame>
  )
}
